/**
 * Google Analytics 4 wrapper.
 *
 * The gtag stub is installed in index.html (so config + pageview suppression
 * can run before React boots). This module exposes a typed surface for the
 * conversion events we care about and pushes events into `window.dataLayer`.
 * No-ops cleanly when dataLayer is missing (tests, SSR, ad blockers).
 *
 * NOTE: we do not pass `send_to: GA_ID` on events. With a single GA4 property
 * configured in index.html, gtag routes events to it automatically, and when
 * the config hasn't finished initializing an explicit `send_to` causes GA4
 * to silently drop the hit.
 */

function gtag(...args: unknown[]) {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === 'function') {
    w.gtag(...args);
  } else if (window.dataLayer) {
    window.dataLayer.push(args);
  }
}

/** SPA page view — fire on every route change (initial load is suppressed
 *  via `send_page_view: false` in the index.html bootstrap). */
export function trackPageView(path: string, title?: string) {
  if (typeof window === 'undefined') return;
  gtag('event', 'page_view', {
    page_location: window.location.href,
    page_path: path,
    page_title: title ?? document.title,
  });
}

/**
 * Fire-and-forget. GA4's internal queue persists across SPA route changes
 * (the JS context survives React Router pushes) and will flush the hit
 * within ms.
 *
 * Note: `sign_up`, `onboarding_complete`, `first_survey_complete`, and
 * `redemption_initiated` are NOT tracked from the frontend — those go
 * through server-side Measurement Protocol (ga4Service.ts) to avoid
 * ad-blocker / SPA-navigation drops. Firing them here too would double
 * the count.
 */
export function trackFirstSurveyStart() {
  gtag('event', 'first_survey_start', {});
}

export function trackReferralSent() {
  gtag('event', 'referral_sent', {});
}

/** Reads UTM params off the current URL and persists them on the GA session
 *  so subsequent events carry the attribution context. Safe to call on every
 *  mount — only forwards to GA when at least one utm_* param is present. */
export function trackUTMParams() {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
  };
  if (Object.values(utm).some(Boolean)) {
    gtag('set', utm);
  }
}
